/**
 * Non-technical hint when the user needs a kiosk-linked shop URL but we have
 * no remembered kiosk id — avoids navigating back to bare `/shop` (blocked).
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../shared/ui/Button.js';
import { Dialog } from '../../../shared/ui/Dialog.js';
import { useOptionalOverlayBackDismiss } from '../../../shared/context/OverlayBackDismissContext.js';

export interface KioskShopHelpModalProps {
  open: boolean;
  onClose: () => void;
}

export function KioskShopHelpModal({ open, onClose }: KioskShopHelpModalProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const overlayBack = useOptionalOverlayBackDismiss();
  const setOverlayDismiss = overlayBack?.setOverlayDismiss;

  useEffect(() => {
    if (!open || setOverlayDismiss === undefined) {
      return;
    }
    setOverlayDismiss(onClose);
    return () => {
      setOverlayDismiss(null);
    };
  }, [open, onClose, setOverlayDismiss]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('shop.kioskHelpTitle')}
      titleId="kiosk-shop-help-title"
      closeLabel={t('shop.understood')}
      closeOnOverlayClick
      testId="kiosk-shop-help-modal"
      className="max-w-md"
      hideHeader
    >
      <h2
        className="text-lg font-semibold text-[var(--color-on-surface)]"
        aria-hidden="true"
      >
        {t('shop.kioskHelpTitle')}
      </h2>
      <p className="mt-3 text-sm text-[var(--color-on-surface-muted)]">
        {t('shop.kioskHelpBody')}
      </p>
      <div className="mt-4 flex justify-end">
        <Button intent="primary" size="md" type="button" onClick={onClose}>
          {t('shop.understood')}
        </Button>
      </div>
    </Dialog>
  );
}
